import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import type { CreateTestEmployeeBody } from './dataprev-employee.wire';

// Formulário do "Criar CPF para Teste": o schema yup do legado (`CreateTestEmployeeFormSchema.ts`) em
// zod, com as mesmas mensagens, e a conversão para o fio (`formatEmployeeData`,
// `CreateTestEmployeeContainer.tsx:77-123`). Os campos guardam texto como o formulário do legado;
// documentos chegam crus do `MaskedFormField` (U37-8) e datas como dia civil (`YYYY-MM-DD`).

export interface CreateTestEmployeeForm {
  codigoInscricao: string;
  numeroInscricaoEmpregador: string;
  nomeEmpregador: string;
  matricula: string;
  dataAdmissao: string | null;
  dataDesligamento: string | null;
  motivoDesligamento: string;
  elegivel: string;
  motivoInelegibilidade: string;
  cpfTrabalhador: string;
  nomeTrabalhador: string;
  exposicaoPolitica: string;
  sexo: string;
  dataNascimento: string | null;
  codigoCategoriaTrabalhador: string;
  valorTotalVencimentos: string;
  valorBaseMargem: string;
  codigoInscricaoEstabelecimento: string;
  numeroInscricaoEstabelecimento: string;
  classificacaoTributaria: string;
}

/** `defaultValuesCreateTestEmployeeFormSchema` (`CreateTestEmployeeFormSchema.ts:60-84`). */
export const CREATE_TEST_EMPLOYEE_EMPTY: CreateTestEmployeeForm = {
  codigoInscricao: '',
  numeroInscricaoEmpregador: '',
  nomeEmpregador: '',
  matricula: '',
  dataAdmissao: null,
  dataDesligamento: null,
  motivoDesligamento: '',
  elegivel: '',
  motivoInelegibilidade: '',
  cpfTrabalhador: '',
  nomeTrabalhador: '',
  exposicaoPolitica: '',
  sexo: '',
  dataNascimento: null,
  codigoCategoriaTrabalhador: '',
  valorTotalVencimentos: '',
  valorBaseMargem: '',
  codigoInscricaoEstabelecimento: '',
  numeroInscricaoEstabelecimento: '',
  classificacaoTributaria: '',
};

/** Os dez campos que habilitam "Adicionar à Lista" (`CreateTestEmployeeForm.tsx:65-88`). */
export const REQUIRED_FIELDS = [
  'cpfTrabalhador',
  'matricula',
  'numeroInscricaoEmpregador',
  'nomeTrabalhador',
  'sexo',
  'dataNascimento',
  'codigoCategoriaTrabalhador',
  'elegivel',
  'dataAdmissao',
  'nomeEmpregador',
] as const satisfies readonly (keyof CreateTestEmployeeForm)[];

/** "Selecione…" dos selects opcionais: o legado aceita voltar ao vazio; o `Select` não emite `''`. */
export const NONE = 'none';

/** `cleanDocumentValue` (`helpers/validations/formFieldValidator.ts:139-142`): dígitos, ou letras e dígitos com `CNPJ_ALPHANUMERIC`. */
export function cleanDocumentValue(value: string, alphanumeric: boolean): string {
  return alphanumeric ? value.replace(/[^a-zA-Z0-9]/g, '') : value.replace(/\D/g, '');
}

/** Vazio para o fio: `''`, `null`, ausente ou o "Selecione…" (`if (values.campo)` do legado). */
const blankText = (value: string | null | undefined): value is '' | null | undefined => value === undefined || value === null || value === '' || value === NONE;

/** Schema com as mensagens do legado; recebe o `i18n` para sair no idioma ativo (U33). */
export function createTestEmployeeSchema(i18n: I18n, options: { alphanumericCnpj: boolean }) {
  const m = (descriptor: MessageDescriptor) => i18n._(descriptor);
  const required = (message: MessageDescriptor) => z.string().min(1, m(message));
  const requiredDate = (message: MessageDescriptor) => z.string().nullable().refine((value) => value !== null && value !== '', m(message));
  const clean = (value: string) => cleanDocumentValue(value, options.alphanumericCnpj);
  return z
    .object({
      codigoInscricao: z.string(),
      numeroInscricaoEmpregador: required(msg`Número de inscrição do empregador é obrigatório`).refine((value) => clean(value).length === 14, m(msg`CNPJ deve ter 14 caracteres`)),
      nomeEmpregador: required(msg`Nome do empregador é obrigatório`),
      matricula: required(msg`Matrícula é obrigatória`),
      dataAdmissao: requiredDate(msg`Data de admissão é obrigatória`),
      dataDesligamento: z.string().nullable(),
      motivoDesligamento: z.string(),
      elegivel: required(msg`Campo obrigatório`),
      motivoInelegibilidade: z.string(),
      cpfTrabalhador: required(msg`CPF do trabalhador é obrigatório`).refine((value) => value.replace(/\D/g, '').length === 11, m(msg`CPF deve ter 11 dígitos`)),
      nomeTrabalhador: required(msg`Nome do trabalhador é obrigatório`),
      exposicaoPolitica: z.string(),
      sexo: required(msg`Sexo é obrigatório`).refine((value) => value === '1' || value === '2', m(msg`Selecione Masculino ou Feminino`)),
      dataNascimento: requiredDate(msg`Data de nascimento é obrigatória`),
      codigoCategoriaTrabalhador: required(msg`Código da categoria é obrigatório`),
      valorTotalVencimentos: z.string(),
      valorBaseMargem: z.string(),
      codigoInscricaoEstabelecimento: z.string(),
      numeroInscricaoEstabelecimento: z.string().refine((value) => value === '' || clean(value).length === 14, m(msg`CNPJ deve ter 14 caracteres`)),
      classificacaoTributaria: z.string(),
    })
    .superRefine((values, context) => {
      if (values.elegivel === 'false' && values.motivoInelegibilidade === '')
        context.addIssue({ code: 'custom', path: ['motivoInelegibilidade'], message: m(msg`Motivo de inelegibilidade é obrigatório quando não elegível`) });
    });
}

/** `parseInt` do legado: texto não numérico vira `NaN`, que o `JSON.stringify` manda como `null`. */
const legacyInt = (value: string): number => Number.parseInt(value, 10);

/**
 * `formatEmployeeData` (`CreateTestEmployeeContainer.tsx:77-123`), campo a campo: CPF como número
 * (zeros à esquerda somem), CNPJ limpo, códigos por `parseInt`, `elegivel` booleano, datas
 * `YYYY-MM-DD` e os opcionais só quando o texto é verdadeiro (`'0'` vai). Os dois "Código de
 * inscrição" nunca vão ao fio.
 */
export function toCreateTestEmployeeBody(values: CreateTestEmployeeForm, options: { alphanumericCnpj: boolean }): CreateTestEmployeeBody {
  const body: CreateTestEmployeeBody = {
    cpfTrabalhador: legacyInt(values.cpfTrabalhador.replace(/\D/g, '')),
    matricula: values.matricula,
    numeroInscricaoEmpregador: cleanDocumentValue(values.numeroInscricaoEmpregador, options.alphanumericCnpj),
    nome: values.nomeTrabalhador,
    codigoSexo: legacyInt(values.sexo),
    dataNascimento: values.dataNascimento ?? '',
    codigoCategoriaTrabalhador: legacyInt(values.codigoCategoriaTrabalhador),
    elegivel: values.elegivel === 'true',
    dataAdmissao: values.dataAdmissao ?? '',
    nomeEmpregador: values.nomeEmpregador,
  };
  if (!blankText(values.numeroInscricaoEstabelecimento)) body.numeroInscricaoEstabelecimento = cleanDocumentValue(values.numeroInscricaoEstabelecimento, options.alphanumericCnpj);
  if (!blankText(values.motivoInelegibilidade)) body.codigoInelegivel = legacyInt(values.motivoInelegibilidade);
  if (!blankText(values.valorTotalVencimentos)) body.valorTotalVencimentos = legacyInt(values.valorTotalVencimentos);
  if (!blankText(values.valorBaseMargem)) body.valorBaseMargem = legacyInt(values.valorBaseMargem);
  if (!blankText(values.dataDesligamento)) body.dataDesligamento = values.dataDesligamento;
  if (!blankText(values.motivoDesligamento)) body.codigoMotivoDesligamento = legacyInt(values.motivoDesligamento);
  if (!blankText(values.exposicaoPolitica)) body.pessoaExpostaPoliticamente = legacyInt(values.exposicaoPolitica);
  if (!blankText(values.classificacaoTributaria)) body.codigoClassificacaoTributaria = legacyInt(values.classificacaoTributaria);
  return body;
}

